import axios from "axios";
import { auth } from "../firebase";
import { showRateLimitMessage } from "../utils/systemMessageNotifier";
import telemetry from "./telemetry";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

// Sistema de cache TTL para evitar sobrecarregamento
class ApiCache {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutos padrão
  }

  // Gerar chave única para a requisição
  generateKey(config) {
    const { method = "GET", url, params, data } = config;
    const key = `${method.toUpperCase()}:${url}`;

    if (params) {
      const sortedParams = Object.keys(params)
        .sort()
        .map((k) => `${k}=${params[k]}`)
        .join("&");
      return `${key}?${sortedParams}`;
    }

    if (
      data &&
      (method.toUpperCase() === "GET" || method.toUpperCase() === "HEAD")
    ) {
      return `${key}:${JSON.stringify(data)}`;
    }

    return key;
  }

  // Verificar se deve usar cache para esta requisição
  shouldCache(config) {
    const method = (config.method || "GET").toUpperCase();

    // Apenas cachear GET requests por padrão
    if (method !== "GET") return false;

    // Não cachear se explicitamente desabilitado
    if (config.noCache === true) return false;

    // Não cachear requisições com parâmetros sensíveis
    const url = config.url || "";
    const sensitiveEndpoints = ["/auth", "/login", "/logout", "/token"];
    if (sensitiveEndpoints.some((endpoint) => url.includes(endpoint)))
      return false;

    return true;
  }

  // Obter TTL específico baseado no endpoint
  getTTL(config) {
    const url = config.url || "";

    // TTLs específicos para diferentes tipos de dados
    const ttlConfig = {
      // Dados que mudam raramente - TTL longo
      "/stores/me": 30 * 1000, // 10 minutos
      "/stores/firebase": 30 * 1000, // 10 minutos
      "/products": 10 * 60 * 1000, // 10 minutos (aumentado de 5 para evitar 429)
      "/motoboys": 30 * 1000, // 30 segundos
      "/contracts": 30 * 1000, // 30 segundos
      "/stores": 5 * 60 * 1000, // 5 minutos
      "/delivery-price": 30 * 1000, // 15 minutos
      "/admin/firebase": 10 * 60 * 1000, // 10 minutos
      "/admin/dashboard": 1 * 60 * 1000, // 1 minuto

      // Dados que mudam com frequência - TTL curto
      "/orders": 2 * 1000, // 2 segundos
      "/orders/all": 1 * 1000,
      "/notifications": 30 * 1000, // 30 segundos
      "/travels": 0.2 * 60 * 1000, // 1 minuto

      // Chat endpoints - Cache agressivo para prevenir 429
      "/chat/message/has-unread": 120 * 1000, // 2 minutos (bem maior que intervalo)
      "/chat/message/unread": 30 * 1000, // 30 segundos
      "/chat/message/unread-info": 300 * 1000, // 5 minutos (endpoint otimizado - aumentado para prevenir spam)
      "/chat/user": 180 * 1000, // 3 minutos para lista de chats

      // Dados em tempo real - TTL muito curto
      "/motoboys/find": 10 * 1000, // 10 segundos
      "update-location": 5 * 1000, // 5 segundos
    };

    // Verificar se há configuração específica
    for (const [endpoint, ttl] of Object.entries(ttlConfig)) {
      if (url.includes(endpoint)) {
        return ttl;
      }
    }

    // Usar TTL customizado se especificado
    if (config.cacheTTL) return config.cacheTTL;

    return this.defaultTTL;
  }

  // Obter do cache
  get(key) {
    const item = this.cache.get(key);

    if (!item) return null;

    // Verificar se expirou
    if (Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    // Log telemetria de cache hit
    if (process.env.NODE_ENV === "development") {
      telemetry.logCacheEvent({
        type: "hit",
        endpoint: key,
        cachedAt: new Date(item.cachedAt).toISOString(),
        expiresAt: new Date(item.expiresAt).toISOString(),
      });
    }

    // Clonar para evitar mutação
    return {
      data: JSON.parse(JSON.stringify(item.data)),
      status: item.status,
      statusText: item.statusText,
      headers: { ...item.headers },
      config: { ...item.config },
      fromCache: true,
      cachedAt: item.cachedAt,
    };
  }

  // Armazenar no cache
  set(key, response, ttl) {
    const item = {
      data: JSON.parse(JSON.stringify(response.data)),
      status: response.status,
      statusText: response.statusText,
      headers: { ...response.headers },
      config: { ...response.config },
      cachedAt: Date.now(),
      expiresAt: Date.now() + ttl,
    };

    this.cache.set(key, item);

    // Log em desenvolvimento
    if (process.env.NODE_ENV === "development") {
      // console.log(`🗄️ Cache SET: ${key} (TTL: ${ttl}ms)`);
    }
  }

  // Invalidar cache para uma chave específica
  invalidate(keyPattern) {
    let invalidatedCount = 0;

    for (const [key] of this.cache) {
      if (key.includes(keyPattern)) {
        this.cache.delete(key);
        invalidatedCount++;
      }
    }

    if (process.env.NODE_ENV === "development" && invalidatedCount > 0) {
      console.log(
        `🗑️ Cache invalidated: ${invalidatedCount} entries for pattern "${keyPattern}"`
      );
    }

    return invalidatedCount;
  }

  // Limpar cache expirado
  cleanup() {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [key, item] of this.cache) {
      if (now > item.expiresAt) {
        this.cache.delete(key);
        cleanedCount++;
      }
    }

    if (process.env.NODE_ENV === "development" && cleanedCount > 0) {
      // console.log(`🧹 Cache cleanup: ${cleanedCount} expired entries removed`);
    }

    return cleanedCount;
  }

  // Limpar todo o cache
  clear() {
    const size = this.cache.size;
    this.cache.clear();

    if (process.env.NODE_ENV === "development") {
      // console.log(`🗑️ Cache cleared: ${size} entries removed`);
    }

    return size;
  }

  // Obter estatísticas do cache
  getStats() {
    const now = Date.now();
    let expired = 0;
    let active = 0;

    for (const [, item] of this.cache) {
      if (now > item.expiresAt) {
        expired++;
      } else {
        active++;
      }
    }

    return {
      total: this.cache.size,
      active,
      expired,
      memory: this.getMemoryUsage(),
    };
  }

  // Estimar uso de memória (aproximado)
  getMemoryUsage() {
    let bytes = 0;

    for (const [key, item] of this.cache) {
      bytes += key.length * 2; // String chars = 2 bytes
      bytes += JSON.stringify(item).length * 2;
    }

    return {
      bytes,
      kb: Math.round(bytes / 1024),
      mb: Math.round((bytes / 1024 / 1024) * 100) / 100,
    };
  }
}

// Sistema de fila para controle de rate limiting com backoff exponencial
class ApiQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
    this.maxConcurrent = 3; // Máximo de requisições simultâneas
    this.minDelay = 2000; // Delay mínimo entre requisições (ms)
    this.retryAttempts = 3; // Máximo de tentativas (aumentado para backoff)
    this.activeRequests = 0;
    this.baseRetryDelay = 5000; // Delay base para backoff exponencial (5s)
    this.maxRetryDelay = 30000; // Delay máximo para backoff (30s)
    this.jitterEnabled = true; // Adicionar jitter para evitar thundering herd
    this.inflightRequests = new Map(); // Rastrear requisições em flight para deduplicação
    this.endpointLimits = new Map(); // Limites por endpoint
    this.lastRequestTime = new Map(); // Rastrear última requisição por endpoint
    this.setupBroadcastChannel(); // Sincronização entre abas
  }

  // Configurar BroadcastChannel para sincronizar cache entre abas
  setupBroadcastChannel() {
    try {
      if (typeof BroadcastChannel !== "undefined") {
        this.channel = new BroadcastChannel("gringo-api-queue");
        this.channel.onmessage = (event) => {
          const { type, endpoint, data } = event.data;
          if (type === "CACHE_UPDATE" && endpoint) {
            // Notificar que cache foi atualizado em outra aba
            if (process.env.NODE_ENV === "development") {
              console.log(
                `📡 Cache update from another tab: ${endpoint}`,
                data
              );
            }
          }
        };
        console.log("📡 BroadcastChannel initialized for API queue");
      }
    } catch (error) {
      console.warn(
        "BroadcastChannel não disponível neste navegador:",
        error.message
      );
    }
  }

  // Notificar outras abas sobre atualização de cache
  broadcastCacheUpdate(endpoint, data) {
    if (this.channel) {
      try {
        this.channel.postMessage({
          type: "CACHE_UPDATE",
          endpoint,
          data,
          timestamp: Date.now(),
        });
      } catch (error) {
        console.warn("Erro ao enviar mensagem via BroadcastChannel:", error);
      }
    }
  }

  async add(requestConfig) {
    // Implementar deduplicação para requisições GET idênticas
    const requestKey = this.generateRequestKey(requestConfig);

    // Se requisição já está em flight, reutilizar a promise
    if (this.inflightRequests.has(requestKey)) {
      if (process.env.NODE_ENV === "development") {
        console.log(`♻️ Deduplicando requisição: ${requestKey}`);
        // Log telemetria de deduplicação
        const endpoint = new URL(requestConfig.url, API_URL).pathname;
        telemetry.logDeduplication({
          requestKey,
          endpoint,
          savedCalls: 1,
        });
      }
      return this.inflightRequests.get(requestKey);
    }

    const promise = new Promise((resolve, reject) => {
      this.queue.push({
        config: requestConfig,
        resolve,
        reject,
        attempts: 0,
        timestamp: Date.now(),
        requestKey,
      });

      this.processQueue();
    });

    // Se é GET request, armazenar promise em flight
    if (requestConfig.method === "GET" || !requestConfig.method) {
      this.inflightRequests.set(requestKey, promise);
      promise.finally(() => {
        this.inflightRequests.delete(requestKey);
      });
    }

    return promise;
  }

  async processQueue() {
    if (
      this.processing ||
      this.queue.length === 0 ||
      this.activeRequests >= this.maxConcurrent
    ) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0 && this.activeRequests < this.maxConcurrent) {
      const item = this.queue.shift();
      this.executeRequest(item);
    }

    this.processing = false;
  }

  async executeRequest(item) {
    this.activeRequests++;

    try {
      // Verificar limite por endpoint antes de executar
      const endpoint = new URL(item.config.url, API_URL).pathname;
      if (!this.checkEndpointLimit(endpoint)) {
        throw new Error(`Rate limit atingido para endpoint: ${endpoint}`);
      }

      // Aguardar delay mínimo entre requisições
      const timeSinceLastRequest = Date.now() - item.timestamp;
      if (timeSinceLastRequest < this.minDelay) {
        await this.delay(this.minDelay - timeSinceLastRequest);
      }

      const response = await axios(item.config);

      // Broadcast cache update para outras abas
      const endpoint_for_broadcast = new URL(item.config.url, API_URL).pathname;
      if (item.config.method === "GET" || !item.config.method) {
        this.broadcastCacheUpdate(endpoint_for_broadcast, {
          status: response.status,
          timestamp: Date.now(),
        });
      }

      item.resolve(response);
    } catch (error) {
      // Verificar se deve fazer retry com backoff exponencial
      if (this.shouldRetry(error, item)) {
        item.attempts++;
        const retryDelay = this.calculateBackoffDelay(item.attempts);

        // Melhorar log com telemetria
        const endpoint = new URL(item.config.url, API_URL).pathname;
        console.warn(
          `[RETRY] ${
            error.response?.status || "NETWORK"
          } - Endpoint: ${endpoint} - Tentativa ${item.attempts}/${
            this.retryAttempts
          } em ${retryDelay}ms`
        );

        // Mostrar mensagem amigável ao usuário para rate limiting
        if (error.response?.status === 429 && item.attempts === 1) {
          showRateLimitMessage();
          // Log detalhado de 429 com telemetria
          const endpoint = new URL(item.config.url, API_URL).pathname;
          telemetry.log429Error({
            endpoint,
            attempt: item.attempts,
            retryDelay,
            retryAfter: error.response.headers["retry-after"],
            statusCode: error.response.status,
            requestKey: item.requestKey,
          });
          console.error("[429 RATE LIMIT]", {
            endpoint,
            attempt: item.attempts,
            retryDelay,
            timestamp: new Date().toISOString(),
            retryAfter: error.response.headers["retry-after"],
          });
        }

        setTimeout(() => {
          item.timestamp = Date.now();
          this.queue.unshift(item); // Adicionar no início da fila
          this.processQueue();
        }, retryDelay);
      } else {
        item.reject(error);
      }
    } finally {
      this.activeRequests--;

      // Processar próximos itens da fila após delay
      setTimeout(() => {
        this.processQueue();
      }, this.minDelay);
    }
  }

  // Verificar se deve fazer retry baseado no erro e número de tentativas
  shouldRetry(error, item) {
    if (item.attempts >= this.retryAttempts) {
      return false;
    }

    // Não fazer retry se foi cancelado manualmente
    if (error.message === "Fila limpa") {
      return false;
    }

    const status = error.response?.status;

    // Fazer retry para erros transitórios
    const retryableStatuses = [
      408, // Request Timeout
      429, // Too Many Requests
      500, // Internal Server Error
      502, // Bad Gateway
      503, // Service Unavailable
      504, // Gateway Timeout
      522, // Connection timed out
      524, // A timeout occurred
    ];

    // Fazer retry para erros de rede
    const networkErrors = [
      "ECONNRESET",
      "ENOTFOUND",
      "ECONNREFUSED",
      "ETIMEDOUT",
      "EAI_AGAIN",
    ];

    return (
      retryableStatuses.includes(status) ||
      networkErrors.some((code) => error.code === code) ||
      error.message.includes("Network Error") ||
      error.message.includes("timeout")
    );
  }

  // Calcular delay com backoff exponencial e jitter
  calculateBackoffDelay(attempt) {
    // Backoff exponencial: baseDelay * (2 ^ attempt)
    let delay = this.baseRetryDelay * Math.pow(2, attempt - 1);

    // Aplicar limite máximo
    delay = Math.min(delay, this.maxRetryDelay);

    // Adicionar jitter (±25%) para evitar thundering herd
    if (this.jitterEnabled) {
      const jitter = delay * 0.25 * (Math.random() * 2 - 1);
      delay = Math.max(0, delay + jitter);
    }

    return Math.round(delay);
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Limpar fila em caso de emergência
  clear() {
    this.queue.forEach((item) => {
      item.reject(new Error("Fila limpa"));
    });
    this.queue = [];
  }

  // Obter estatísticas da fila
  getStats() {
    return {
      queueLength: this.queue.length,
      activeRequests: this.activeRequests,
      processing: this.processing,
      maxConcurrent: this.maxConcurrent,
      retryAttempts: this.retryAttempts,
      baseRetryDelay: this.baseRetryDelay,
      maxRetryDelay: this.maxRetryDelay,
      jitterEnabled: this.jitterEnabled,
    };
  }

  // Configurar parâmetros do backoff exponencial
  configureBackoff(options = {}) {
    if (options.baseRetryDelay !== undefined) {
      this.baseRetryDelay = Math.max(100, options.baseRetryDelay);
    }
    if (options.maxRetryDelay !== undefined) {
      this.maxRetryDelay = Math.max(1000, options.maxRetryDelay);
    }
    if (options.retryAttempts !== undefined) {
      this.retryAttempts = Math.max(1, Math.min(10, options.retryAttempts));
    }
    if (options.jitterEnabled !== undefined) {
      this.jitterEnabled = options.jitterEnabled;
    }

    console.log("🔧 Backoff exponencial configurado:", {
      baseRetryDelay: this.baseRetryDelay,
      maxRetryDelay: this.maxRetryDelay,
      retryAttempts: this.retryAttempts,
      jitterEnabled: this.jitterEnabled,
    });
  }

  // Configurar limites por endpoint (estratégia 4)
  setEndpointLimit(endpoint, maxRequests, timeWindowMs) {
    this.endpointLimits.set(endpoint, {
      maxRequests,
      timeWindowMs,
      requests: [],
    });
    console.log(
      `🚦 Rate limit configurado para ${endpoint}: ${maxRequests} req/${timeWindowMs}ms`
    );
  }

  // Verificar se endpoint atingiu seu limite
  checkEndpointLimit(endpoint) {
    const limit = this.endpointLimits.get(endpoint);
    if (!limit) return true; // Sem limite configurado

    const now = Date.now();
    limit.requests = limit.requests.filter(
      (time) => now - time < limit.timeWindowMs
    );

    if (limit.requests.length >= limit.maxRequests) {
      return false; // Atingiu o limite
    }

    limit.requests.push(now);
    return true;
  }

  // Gerar chave única para deduplicação (estratégia 2)
  generateRequestKey(config) {
    const { method = "GET", url, params, data } = config;
    let key = `${method.toUpperCase()}:${url}`;

    if (params) {
      const sortedParams = Object.keys(params)
        .sort()
        .map((k) => `${k}=${params[k]}`)
        .join("&");
      key += `?${sortedParams}`;
    }

    return key;
  }
}

const apiQueue = new ApiQueue();
const apiCache = new ApiCache();

// Limpar cache expirado periodicamente
setInterval(() => {
  apiCache.cleanup();
}, 5 * 60 * 1000); // A cada 5 minutos

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para adicionar token de autenticação e verificar cache
api.interceptors.request.use(
  async (config) => {
    // Se for FormData (upload), não forçar Content-Type JSON (deixa o axios setar boundary)
    try {
      if (
        typeof FormData !== "undefined" &&
        config?.data &&
        config.data instanceof FormData
      ) {
        if (
          config.headers?.set &&
          typeof config.headers.delete === "function"
        ) {
          config.headers.delete("Content-Type");
          config.headers.delete("content-type");
        } else {
          config.headers = config.headers || {};
          delete config.headers["Content-Type"];
          delete config.headers["content-type"];
        }
      }
    } catch (e) {
      // Não bloquear request por erro de detecção
    }

    // Verificar cache antes de fazer a requisição
    if (apiCache.shouldCache(config)) {
      const cacheKey = apiCache.generateKey(config);
      const cachedResponse = apiCache.get(cacheKey);

      if (cachedResponse) {
        if (process.env.NODE_ENV === "development") {
          // console.log(`🗄️ Cache HIT: ${config.url}`);
        }

        // Para cache hit, precisamos cancelar a requisição e retornar o valor do cache
        const cancelError = new Error("Cache hit");
        cancelError.config = config;
        cancelError.cachedResponse = cachedResponse;
        cancelError.isCache = true;
        throw cancelError;
      }

      // Marcar para cache na resposta
      config._shouldCache = true;
      config._cacheKey = cacheKey;
      config._cacheTTL = apiCache.getTTL(config);
    }

    // Adicionar token de autenticação
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        if (process.env.NODE_ENV !== "production") {
          // console.log("Token de autenticação:", token);
        }
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
        const nodeEnv = (process.env.NODE_ENV || "").trim().toLowerCase();
      } catch (tokenError) {
        console.error("Erro ao obter token:", tokenError);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de resposta para lidar com rate limiting e cache
api.interceptors.response.use(
  (response) => {
    // Armazenar no cache se necessário
    if (response.config._shouldCache && response.config._cacheKey) {
      apiCache.set(
        response.config._cacheKey,
        response,
        response.config._cacheTTL
      );

      if (process.env.NODE_ENV === "development") {
        // console.log(`🗄️ Cache MISS -> SET: ${response.config.url}`);
      }
    }

    return response;
  },
  async (error) => {
    // Verificar se é um cache hit
    if (error.isCache && error.cachedResponse) {
      return Promise.resolve(error.cachedResponse);
    }

    const config = error.config;

    // Se é um erro que pode ser retentado e não foi marcado como retry
    if (apiQueue.shouldRetry(error, { attempts: 0 }) && !config._isRetry) {
      config._isRetry = true;

      // Mostrar mensagem amigável ao usuário para rate limiting
      if (error.response?.status === 429) {
        showRateLimitMessage();
        // Log telemetria de retry para 429
        telemetry.logRetry({
          endpoint: config.url,
          statusCode: error.response.status,
          attemptNumber: 1,
          maxAttempts: this.retryAttempts,
          delayMs: delayTime,
          reason: "Rate limit (429)",
          retryAfter: error.response.headers["retry-after"],
        });
      }

      // Extrair tempo de retry do header se disponível (para 429)
      let delayTime;
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers["retry-after"];
        delayTime = retryAfter
          ? parseInt(retryAfter) * 1000
          : apiQueue.baseRetryDelay;
      } else {
        delayTime = apiQueue.baseRetryDelay;
      }

      if (error.response?.status !== 429) {
        return Promise.reject(error);
      }

      console.warn(
        `Erro ${
          error.response?.status || "network"
        } detectado. Aguardando ${delayTime}ms antes de tentar novamente...`
      );

      // Aguardar antes de tentar novamente
      await new Promise((resolve) => setTimeout(resolve, delayTime));

      // Adicionar à fila para retry com backoff exponencial
      return apiQueue.add(config);
    }

    return Promise.reject(error);
  }
);

// Sobrescrever métodos do axios para usar a fila
const originalRequest = api.request;
api.request = function (config) {
  // Para requisições críticas que precisam da fila
  if (config.useQueue !== false) {
    return apiQueue.add(config);
  }
  // Para requisições que não precisam da fila (ex: health checks)
  return originalRequest.call(this, config);
};

// Serviços do Usuário
export const registerUser = async (userData) => {
  return api.post("/stores/register", userData);
};

export const createUserProfile = async (userData) => {
  return api.post("/stores/profile", userData);
};

export const getUserProfile = async () => {
  return api.get("/stores/me");
};

export const getTax = async () => {
  return api.get("/stores/marketplace/tax");
};

// Taxas de pagamento do marketplace (Pix, cartão, boleto)
export const getMarketplaceFees = async () => {
  return api.get("/marketplace/taxas");
};

// === Repasse Marketplace (Admin) ===
// Agrupado por loja com valor de repasse por status de pagamento
export const getMarketplaceFinanceByStore = async () => {
  return api.get("/marketplace/orders/finance/by-store", { noCache: true });
};

// Resumo financeiro por loja, com filtro opcional de status (ex: "pendente,pago")
export const getMarketplaceFinanceSummary = async (status) => {
  return api.get("/marketplace/orders/finance/summary", {
    params: status ? { status } : undefined,
    noCache: true,
  });
};

// Pedidos pendentes de repasse para uma loja
export const getMarketplacePendingOrders = async (storeId) => {
  return api.get("/marketplace/pending/orders", {
    params: { storeId },
    noCache: true,
  });
};

// Dispara o repasse via PIX dos pedidos pendentes de uma loja
export const payMarketplacePendingOrders = async (storeId) => {
  return api.post("/marketplace/pending/orders/pay", { storeId });
};

export const updateUserProfile = async (profileData) => {
  return api.post("/stores/profile", profileData);
};

// Serviços de Produtos
export const getProducts = async ({ limit = 25, skip = 0 } = {}) => {
  return api.get("/products", { params: { limit, skip }, noCache: true });
};

export const getProductById = async (id) => {
  return api.get(`/products/${id}`);
};

export const createProduct = async (productData) => {
  return api.post("/products/create", productData);
};

export const updateProduct = async (id, productData) => {
  return api.put(`/products/${id}`, productData);
};

export const deleteProduct = async (id) => {
  return api.delete(`/products/${id}`);
};

// Serviços de Pedidos
export const getOrders = async () => {
  return api.get("/orders");
};

export const getOrderById = async (id) => {
  return api.get(`/orders/${id}`);
};

export const createOrder = async (orderData) => {
  return api.post("/orders", orderData);
};

export const updateOrderStatus = async (id, status) => {
  return api.put("/orders/status", { id, status });
};

// === Serviços PDV (Caixa) ===
export const pdvAbrirCaixa = async (storeId, data) => {
  return api.post(`/pdv/${storeId}/abrir`, data, { noCache: true });
};

export const pdvCaixaAtual = async (storeId) => {
  return api.get(`/pdv/${storeId}/atual`, { noCache: true });
};

export const pdvHistorico = async (storeId, params = {}) => {
  return api.get(`/pdv/${storeId}/historico`, { params, noCache: true });
};

export const pdvDetalhe = async (caixaId) => {
  return api.get(`/pdv/detalhe/${caixaId}`, { noCache: true });
};

export const pdvMovimentacao = async (caixaId, data) => {
  return api.post(`/pdv/${caixaId}/movimentacao`, data, { noCache: true });
};

export const pdvFecharCaixa = async (caixaId, data) => {
  return api.post(`/pdv/${caixaId}/fechar`, data, { noCache: true });
};

export const criarPedidoPDV = async (orderData) => {
  return api.post("/orders/pdv", orderData, { noCache: true });
};

// === Serviços PDV (Funcionários do Caixa) ===
export const pdvCriarFuncionario = async (storeId, data) => {
  return api.post(`/pdv/${storeId}/funcionarios`, data, { noCache: true });
};

export const pdvListarFuncionarios = async (storeId) => {
  return api.get(`/pdv/${storeId}/funcionarios`, { noCache: true });
};

// Tempo de entrega dos pedidos da loja (rota /orders/avg-time/store)
export const getStoreAvgDeliveryTime = async (params = {}) => {
  return api.get("/orders/avg-time/store", { params });
};

// Tempo de entrega agregado por loja (rota /orders/avg-time/admin)
export const getAdminAvgDeliveryTime = async (params = {}) => {
  return api.get("/orders/avg-time/admin", { params });
};

// Serviços de Motoboy
export const getMotoboys = async () => {
  return api.get("/motoboys");
};
export const getMotoboy = async (id) => {
  return api.get(`/motoboys/id/${id}`);
};

// Serviços de preferências de entregadores
export const getDriverPreferences = async () => {
  return api.get("/stores/driver-preferences");
};

export const updateDriverPreferences = async (
  preferedDriver,
  blacklistDrivers
) => {
  return api.put("/stores/driver-preferences", {
    preferedDriver,
    blacklistDrivers,
  });
};

export const getMotoboyByFirebaseUid = async (firebaseUid) => {
  return api.get(`/motoboys/firebase/${firebaseUid}`);
};

export const getTravelsMotoboy = async (motoboyId) => {
  return api.get(`/travels/${motoboyId}`);
};

export const findMotoboys = async (orderId) => {
  return api.get("/motoboys/find", {
    params: {
      order_id: orderId,
    },
  });
};

export const orderReady = async (motoboyId, orderId) => {
  return api.post("/notifications/order-ready", {
    motoboyId: motoboyId,
    orderId: orderId,
  });
};

export const updateMotoboyLocation = async (locationData) => {
  return api.put("/motoboys/update-location", locationData);
};

// Serviços de Contratos
export const getContracts = async (options = {}) => {
  return api.get("/contracts", options);
};

export const getContract = async (id) => {
  return api.get(`/contracts/${id}`);
};

export const createContract = async (contractData) => {
  return api.post("/contracts", contractData);
};

export const updateContract = async (id, contractData) => {
  return api.put(`/contracts/${id}`, contractData);
};

export const assignMotoboyToContract = async (id, motoboyId) => {
  return api.patch(`/contracts/${id}/assign-motoboy`, { motoboy: motoboyId });
};

export const removeMotoboyFromContract = async (id) => {
  return api.patch(`/contracts/${id}/remove-motoboy`);
};

export const deleteContract = async (id) => {
  return api.delete(`/contracts/${id}`);
};

export const notifyContractsMass = async ({ title, message, motoboyIds }) => {
  return api.post("/contracts/notify-mass", { title, message, motoboyIds });
};

// Função utilitária para converter endereço em coordenadas usando a API do Google Maps
// export const geocodeAddress = async (address) => {
//   try {
//     // Nota: Em produção, você usaria seu próprio serviço proxy para proteger sua API key
//     // Aqui estamos usando uma abordagem simplificada para fins de demonstração
//     const apiKey = process.env.EXPO_PUBLIC_MAPS_API_KEY;
//     const encodedAddress = encodeURIComponent(address);
//     const response = await axios.get(
//       `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`
//     );

//     if (response.data.status === "OK" && response.data.results.length > 0) {
//       const location = response.data.results[0].geometry.location;
//       return {
//         latitude: location.lat,
//         longitude: location.lng,
//       };
//     }

//     throw new Error("Não foi possível geocodificar o endereço");
//   } catch (error) {
//     console.error("Erro ao geocodificar endereço:", error);
//     throw error;
//   }
// };

export const changeStoreCoordinates = async (storeId, coordinates) => {
  return api.post(`/stores/coordinates`, { storeId, coordinates });
};

// Serviços de Estabelecimentos
export const getStores = async () => {
  return api.get("/stores");
};

export const getStore = async (id) => {
  return api.get(`/stores/id/${id}`);
};

export const getStoreMultiplier = async (storeId) => {
  return api.get(`/stores/multiplier`, {
    params: { storeId },
    noCache: true,
  });
};

export const getStoreOrders = async (
  storeId,
  limit = 25,
  skip = 0,
  dateI = null,
  dateF = null
) => {
  const params = new URLSearchParams({ limit, skip });
  if (dateI) params.append("dateI", dateI);
  if (dateF) params.append("dateF", dateF);
  return api.get(`/orders/store/${storeId}?${params.toString()}`);
};


export const freeToNavigate = async (storeId) => {
  return api.post(`/stores/freeToNavigate`, { storeId });
};

export const reproveFreeToNavigate = async (storeId) => {
  return api.post(`/stores/reproveFreeToNavigate`, { storeId });
};

export const approveStore = async (storeId) => {
  return api.post(`/stores/approve/${storeId}`);
};

export const reproveStore = async (storeId) => {
  return api.post(`/stores/reprove/${storeId}`);
};

export const updateStoreStatus = async (storeId, status) => {
  return api.put(`/stores/${storeId}/status`, { status });
};

export const updateStoreBilling = async (storeId, billingOptions) => {
  return api.post(`/stores/billingOptions`, { storeId, billingOptions });
};

export const updateCardapioOptIn = async (storeId, cardapioOptIn) => {
  return api.patch(`/stores/cardapioOptIn/${storeId}`, { cardapioOptIn });
};

export const activateMarketplace = async (storeId) => {
  return api.post(`/stores/marketplace/activate/${storeId}`);
};

export const updateStoreName = async (storeId, businessName) => {
  return api.put(`/stores/name`, { storeId, businessName });
};

export const acceptTerms = async () => {
  return api.post(`/stores/accept-terms`);
};

export const acceptTermsOfService = async () => {
  return api.post(`/stores/accept-terms`);
};

// Serviços de Precificação de Entrega
export const getDeliveryPrice = async () => {
  return api.get("/delivery-price");
};

export const updateDeliveryPrice = async (priceData) => {
  return api.put("/delivery-price", priceData);
};

export const createDeliveryPrice = async (priceData) => {
  return api.post("/delivery-price", priceData);
};

// Funções utilitárias para controle da fila
export const getApiQueueStats = () => {
  return apiQueue.getStats();
};

export const clearApiQueue = () => {
  apiQueue.clear();
};

// Configurar limites da fila (opcional)
export const configureApiQueue = (options = {}) => {
  if (options.maxConcurrent) apiQueue.maxConcurrent = options.maxConcurrent;
  if (options.minDelay) apiQueue.minDelay = options.minDelay;
  if (options.retryAttempts) apiQueue.retryAttempts = options.retryAttempts;
};

// Configurar backoff exponencial (nova função)
export const configureBackoff = (options = {}) => {
  return apiQueue.configureBackoff(options);
};

// Configurações pré-definidas de backoff para diferentes cenários
export const setBackoffProfile = (profile) => {
  const profiles = {
    // Perfil conservador - para aplicações críticas
    conservative: {
      baseRetryDelay: 2000, // 2s
      maxRetryDelay: 60000, // 60s
      retryAttempts: 5,
      jitterEnabled: true,
    },

    // Perfil padrão - balanceado
    balanced: {
      baseRetryDelay: 1000, // 1s
      maxRetryDelay: 30000, // 30s
      retryAttempts: 5,
      jitterEnabled: true,
    },

    // Perfil agressivo - para aplicações que precisam de resposta rápida
    aggressive: {
      baseRetryDelay: 500, // 0.5s
      maxRetryDelay: 15000, // 15s
      retryAttempts: 3,
      jitterEnabled: true,
    },

    // Perfil mínimo - para recursos não críticos
    minimal: {
      baseRetryDelay: 1000, // 1s
      maxRetryDelay: 10000, // 10s
      retryAttempts: 2,
      jitterEnabled: false,
    },
  };

  const config = profiles[profile];
  if (config) {
    configureBackoff(config);
    console.log(`🎯 Perfil de backoff "${profile}" aplicado:`, config);
  } else {
    console.warn(
      `⚠️ Perfil de backoff "${profile}" não encontrado. Perfis disponíveis:`,
      Object.keys(profiles)
    );
  }
};

// Funções utilitárias para controle do cache
export const getCacheStats = () => {
  return apiCache.getStats();
};

export const clearCache = () => {
  return apiCache.clear();
};

export const invalidateCache = (pattern) => {
  return apiCache.invalidate(pattern);
};

export const cleanupCache = () => {
  return apiCache.cleanup();
};

// Configurar cache (opcional)
export const configureCacheDefaults = (ttlMs) => {
  apiCache.defaultTTL = ttlMs;
};

// Função para requisições sem cache
export const noCacheRequest = (config) => {
  return api.request({ ...config, noCache: true, useQueue: false });
};

export const getStoreNotifications = async (firebaseUid) => {
  return api.get(`/notifications/store?firebaseUid=${firebaseUid}`);
};

export const updateStoreNotification = async (id, status) => {
  return api.put("/notifications/store", { id, status });
};

export const markNotificationAsRead = async (id) => {
  return api.patch(`/notifications/mark-as-read/${id}`, { id, status: "READ" });
};

export const notifyStore = async (
  title,
  message,
  storeFirebaseUid,
  type = "STORE_ALERT",
  data = {}
) => {
  return api.post("/notifications/notifyStore", {
    title,
    message,
    storeFirebaseUid,
    type,
    data,
  });
};

// Função para requisições com cache customizado
export const cachedRequest = (config, ttlMs) => {
  return api.request({ ...config, cacheTTL: ttlMs });
};

// Função para invalidar cache de um endpoint específico
export const invalidateCacheForEndpoint = (endpoint) => {
  return apiCache.invalidate(endpoint);
};

export const prefetchData = async (endpoints = []) => {
  const prefetchPromises = endpoints.map(async (endpoint) => {
    try {
      await api.get(endpoint.url, {
        ...endpoint.config,
        cacheTTL: endpoint.ttl || apiCache.defaultTTL,
      });
      // console.log(`✅ Prefetch success: ${endpoint.url}`);
    } catch (error) {
      console.warn(`⚠️ Prefetch failed: ${endpoint.url}`, error.message);
    }
  });

  return Promise.allSettled(prefetchPromises);
};

// Função para requisições prioritárias (bypass da fila)
export const priorityRequest = (config) => {
  return api.request({ ...config, useQueue: false });
};

// Função para requisições em lote com controle de rate limit
export const batchRequest = async (
  requests,
  batchSize = 5,
  delayBetweenBatches = 1000
) => {
  const results = [];

  for (let i = 0; i < requests.length; i += batchSize) {
    const batch = requests.slice(i, i + batchSize);
    const batchPromises = batch.map((request) => api.request(request));

    try {
      const batchResults = await Promise.allSettled(batchPromises);
      results.push(...batchResults);

      // Delay entre lotes para evitar rate limiting
      if (i + batchSize < requests.length) {
        await new Promise((resolve) =>
          setTimeout(resolve, delayBetweenBatches)
        );
      }
    } catch (error) {
      console.error("Erro no lote:", error);
      results.push({ status: "rejected", reason: error });
    }
  }

  return results;
};

// === FUNÇÕES DE TELEMETRIA ===
// Obter relatório de telemetria
export const getTelemetryReport = () => {
  return telemetry.getFullReport();
};

// Obter resumo de erros 429
export const get429Summary = () => {
  return telemetry.get429Summary();
};

// Obter resumo de cache
export const getCacheSummary = () => {
  return telemetry.getCacheSummary();
};

// Obter resumo de deduplicação
export const getDeduplicationSummary = () => {
  return telemetry.getDeduplicationSummary();
};

// Enviar telemetria para o servidor
export const flushTelemetry = () => {
  return telemetry.flushToServer();
};

// Exportar relatório em JSON
export const exportTelemetryJSON = () => {
  return telemetry.exportJSON();
};

// Exportar relatório em CSV
export const exportTelemetryCSV = () => {
  return telemetry.exportCSV();
};

// Descarregar relatório
export const downloadTelemetryReport = (format = "json") => {
  telemetry.downloadReport(format);
};

// Limpar telemetria
export const clearTelemetry = () => {
  telemetry.clear();
};

// === CHAT FUNCTIONS WITH OPTIMIZED CACHING ===
// Verificar se há mensagens de chat não lidas (otimizado)
export const hasUnreadChatMessages = async (userId) => {
  if (!userId || userId.trim() === "") {
    throw new Error(
      "userId é obrigatório para verificar mensagens de chat não lidas"
    );
  }

  // O cache será aplicado automaticamente pelo interceptor (45 segundos)
  return api.get(`/chat/message/has-unread/${userId}`);
};

// Obter contagem de mensagens não lidas (otimizado)
export const getUnreadChatCount = async (userId) => {
  if (!userId || userId.trim() === "") {
    throw new Error(
      "userId é obrigatório para obter contagem de mensagens não lidas"
    );
  }

  // O cache será aplicado automaticamente pelo interceptor (30 segundos)
  return api.get(`/chat/message/unread/${userId}`);
};

// Obter chats do usuário (otimizado)
export const getUserChats = async (userId) => {
  if (!userId || userId.trim() === "") {
    throw new Error("userId é obrigatório para obter chats");
  }

  // O cache será aplicado automaticamente pelo interceptor (60 segundos)
  return api.get(`/chat/user/${userId}`);
};

// Obter informações de mensagens não lidas (endpoint otimizado)
export const getUnreadChatInfo = async (userId) => {
  if (!userId || userId.trim() === "") {
    throw new Error(
      "userId é obrigatório para obter informações de mensagens não lidas"
    );
  }

  // O cache será aplicado automaticamente pelo interceptor (30 segundos)
  return api.get(`/chat/message/unread-info/${userId}`);
};

// Função otimizada para verificar mensagens não lidas com logs de cache
export const checkUnreadChatMessagesOptimized = async (userId) => {
  if (!userId || userId.trim() === "") {
    throw new Error("userId é obrigatório");
  }

  try {
    // Verificar status do cache
    const cacheKey = `GET:/chat/message/has-unread/${userId}`;
    const cacheStats = apiCache.getStats();

    if (process.env.NODE_ENV === "development") {
      console.log(
        `💡 Cache stats: ${cacheStats.active} ativo, ${cacheStats.expired} expirado`
      );
    }

    // A requisição usará automaticamente o cache se disponível
    const response = await hasUnreadChatMessages(userId);
    return response;
  } catch (error) {
    console.error("Erro ao verificar mensagens de chat não lidas:", error);
    throw error;
  }
};

// Invalidar cache de chat quando uma ação importante acontece
export const invalidateChatCache = (userId) => {
  if (userId) {
    const invalidatedCount = apiCache.invalidate(
      `/chat/message/has-unread/${userId}`
    );
    invalidatedCount += apiCache.invalidate(`/chat/message/unread/${userId}`);
    invalidatedCount += apiCache.invalidate(
      `/chat/message/unread-info/${userId}`
    );
    invalidatedCount += apiCache.invalidate(`/chat/user/${userId}`);

    if (process.env.NODE_ENV === "development") {
      console.log(
        `🧹 Cache de chat invalidado: ${invalidatedCount} entradas para usuário ${userId.substring(
          0,
          8
        )}...`
      );
    }

    return invalidatedCount;
  }
  return 0;
};

// Registrar metadado de acesso/clique em uma página
export const saveMetadados = async (data) => {
  return api.post("/metadados", data, { noCache: true });
};

// Utilitários para monitoramento de cache de chat
export const getChatCacheStats = () => {
  const stats = apiCache.getStats();
  return {
    ...stats,
    chatSpecific: {
      hasUnread: "Cache aplicado automaticamente (45s)",
      unreadCount: "Cache aplicado automaticamente (30s)",
      unreadInfo: "Cache aplicado automaticamente (30s)",
      userChats: "Cache aplicado automaticamente (60s)",
    },
  };
};

export default api;
